"use client";
import Image from "next/image";
import styles from "./ChatFeed.module.scss";
import {
  Images,
  SEND_ICON,
  UPLOAD_FILE_ICON,
} from "@/utils/imageRelativePaths";
import { useContext, useEffect, useRef, useState } from "react";
import useTimer from "@/customhooks/usetimer";
import * as ANIMATION_DATA from "../../../public/assets/lotties/typing.json";
import Lottie from "react-lottie";
import {
  endChat,
  sendChatmessage,
  uploadMessagesAttachments,
} from "@/services/chat.service";
import { AppContext, ChatMsgContextData } from "@/context/AppContext";
import { paidDMMsgType } from "@/utils/common.types";
import useWarnOnExit from "@/customhooks/useWarnOnExit";
import { convertTo12HourFormat, getMessageFormat } from "@/utils/helper";
import RoomSkeleton from "../RoomsExplore/components/RoomSkeleton/RoomSkeleton";
import Popup from "@/common/components/Popup/Popup";
import LowBalanceChatCart from "@/common/components/LowBalanceChatCart";
import ChatEndPopup from "@/common/components/ChatEndPopup";
import { Config } from "@/utils/config";

const ChatFeed = () => {
  const messagesEndRef: React.RefObject<HTMLDivElement> = useRef(null);
  const [messages, setMessages] = useState<ChatMsgContextData[]>([]);
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(true);
  const [msgInput, setMsgInput] = useState<string>("");
  const [fileData, setFileData] = useState<File | null>(null);
  const [imagePopup, setImagePopup] = useState<string>("");
  const [isLowBalance, setIsLowBalance] = useState<boolean>(false);
  const [isChatEndPopup, setIsChatEndPopup] = useState<boolean>(false);
  const context = useContext(AppContext);
  const {
    paidDmSocket,
    chatData,
    chatMsgData,
    setChatMsgData,
    setChatData,
    setLoader,
    auth,
    setToastData,
    walletBalance,
  } = context ?? {};

  const { token } = auth ?? {};

  const [secondsElapsed, setSecondsElapsed] = useState<{
    hours: number;
    minutes: number;
    seconds: number;
  }>({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    setIsOnline(chatData?.receiver?.attributes.isOnline ?? false);
  }, [chatData?.receiver?.attributes.isOnline]);

  useEffect(() => {
    setIsLowBalance(chatData?.lowBalancePopup ?? false);
  }, [chatData?.lowBalancePopup]);

  useWarnOnExit(() => {
    handleEndChat();
  });

  const { hours, minutes, seconds } = useTimer(
    0,
    (chatData?.session?.timeLeftMs ?? 0) / 1000
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  };

  useEffect(() => {
    if (fileData?.name && chatData?.session?.roomId && token && setToastData) {
      const formData = new FormData();
      formData.append("attachments", fileData);
      uploadMessagesAttachments(token, formData, setToastData)
        .then((res: string) => {
          if (res && chatData?.session?.roomId) {
            const message = getMessageFormat({
              file: res,
              roomId: chatData?.session?.roomId,
              type: paidDMMsgType.ATTACHMENT,
            });
            sendChatmessage(paidDmSocket, { ...message }, setChatMsgData);
          }
          setFileData(null);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [fileData]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    setIsTyping(chatData?.receiver?.attributes?.isTyping ?? false);
  }, [chatData?.receiver?.attributes.isTyping]);

  useEffect(() => {
    if (chatMsgData?.message?.data.systemType) return;
    chatMsgData?.roomId &&
      setMessages((pre) => {
        return [...pre, chatMsgData];
      });

    if (chatData?.session?.roomId && chatMsgData?.message?.data.raw?.info) {
      const welcomeMessage = getMessageFormat({
        roomId: chatData?.session?.roomId,
        value: `    Welcome to AstroLive, astrologer 
          will take a minute to review your details. 
          You may ask your questions in the meanwhile
          `,
        type: paidDMMsgType.DEFAULT,
      });
      setMessages((pre) => {
        return [
          ...pre,
          {
            message: {
              author: chatMsgData?.message?.author,
              data: { ...welcomeMessage.data },
              messageId: `welcome`,
              attributes: chatMsgData?.message?.attributes,
              createdAt: chatMsgData?.message?.createdAt,
              type: welcomeMessage?.type,
            },
            roomId: chatData.session?.roomId,
          } as ChatMsgContextData,
        ];
      });
    }

    setChatMsgData && setChatMsgData({});
  }, [chatMsgData?.message?.messageId]);

  const handleSent = () => {
    if (
      chatData?.session?.roomId &&
      msgInput.trim().length > 0 &&
      setChatMsgData
    ) {
      const message = getMessageFormat({
        value: msgInput,
        roomId: chatData?.session?.roomId,
      });
      sendChatmessage(paidDmSocket, { ...message }, setChatMsgData);
      setMsgInput("");
    }
  };

  const handleMsgInput = (e: React.FormEvent<HTMLInputElement>) => {
    setMsgInput(e.currentTarget.value);
  };

  useEffect(() => {
    setSecondsElapsed({ hours, minutes, seconds });
  }, [hours, minutes, seconds]);

  const handleBack = () => {
    setIsChatEndPopup(true);
  };
  const handleEndChat = () => {
    setLoader && setLoader(true);
    chatData?.session?.roomId &&
      setChatData &&
      void endChat(paidDmSocket, chatData?.session?.roomId, setChatData);
    setLoader && setLoader(false);
  };

  const renderMsgCart = (value: ChatMsgContextData, index: number) => {
    return (
      <div
        key={value.message?.messageId ?? index + 1}
        className={`${styles.message_cart_container} ${
          value.message?.author.userId == chatData?.initiator?.user.userId
            ? styles.owner_message_cart_container
            : ""
        } `}
      >
        <Image
          src={`${Config.MEDIA_URL}/apipublic${
            value.message?.author.photo ?? ""
          }`}
          alt="profile img"
          width={26}
          height={26}
          className={styles.profile_img}
        />
        <div className={styles.message_secton}>
          <div
            className={`${styles.message} ${
              value.message?.type === paidDMMsgType.ATTACHMENT
                ? styles.with_attachment
                : ""
            } ${
              value.message?.messageId === "welcome"
                ? styles.welcome_message
                : ""
            }`}
          >
            {value.message?.type === paidDMMsgType.ATTACHMENT ? (
              <div className={styles.attachment_img}>
                <Image
                  src={value?.message?.data?.urls[0]}
                  alt="img"
                  height={200}
                  width={400}
                  onClick={() =>
                    setImagePopup(value?.message?.data?.urls[0] ?? "")
                  }
                />
              </div>
            ) : (
              value.message?.data.body
            )}
            {value.message?.data.raw && (
              <>
                <span>Bellow are my details:</span>
                <br />
                <span>DOB:{value.message.data.raw.info.dob}</span>
                <br />
                <span>TOB:{value.message.data.raw.info.tob}</span>
                <br />
                <span>Gender:{value.message.data.raw.info.gender}</span>
                <br />
                <span>POB:{value.message.data.raw.info.pob}</span>
              </>
            )}
          </div>
          <div className={styles.time_stamp}>
            {convertTo12HourFormat(value.message?.createdAt ?? "")}
          </div>
        </div>
      </div>
    );
  };

  if (!chatData?.onChat) {
    return <RoomSkeleton />;
  } else {
    return (
      <div className={styles.chat_feed_container}>
        <div className={styles.chat_feed_header}>
          <div className={styles.header_left_With_btn}>
            <div className={styles.back_icon} onClick={handleBack}>
              <Image
                src={Images.iconBackWhite}
                alt="back_Icon"
                width={19}
                height={19}
              />
            </div>
            <div className={styles.header_left}>
              <div
                className={`${styles.image_section} ${
                  !isOnline ? styles.offline : ""
                }`}
              >
                <Image
                  src={`${Config.MEDIA_URL}/apipublic${
                    chatData?.receiver?.user.photo ?? ""
                  }`}
                  alt="profile img"
                  height={56}
                  width={56}
                  className={styles.profile_img}
                />
              </div>
              <div className={styles.details_section}>
                <div className={styles.name_section}>
                  {chatData?.receiver?.user.name}
                </div>
                <div className={styles.description}>
                  <span className={styles.status}>
                    {isOnline ? "Online" : "Offline"}
                  </span>
                  <div className={styles.border} />
                  <span className={styles.price_section}>
                    ₹{chatData?.receiver?.user?.pricePerMinuteChatInInr}/min
                  </span>
                  {(walletBalance?.astroVCard ?? 0) > 0 && (
                    <>
                      <div className={styles.border} />
                      <span className={styles.free_chat}>Free</span>
                    </>
                  )}
                  <div className={styles.timer_section}>
                    {secondsElapsed?.hours?.toString().length > 1
                      ? secondsElapsed?.hours
                      : `0${secondsElapsed?.hours ?? 0}`}
                    :
                    {secondsElapsed?.minutes?.toString().length > 1
                      ? secondsElapsed?.minutes
                      : `0${secondsElapsed?.minutes ?? 0}`}
                    :
                    {secondsElapsed?.seconds?.toString().length > 1
                      ? secondsElapsed?.seconds
                      : `0${secondsElapsed?.seconds ?? 0}`}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.header_right}>
            <button
              className={styles.end_btn}
              onClick={() => setIsChatEndPopup(true)}
            >
              End
            </button>
          </div>
        </div>
        <div className={styles.chat_feed_body}>
          <div className={styles.chat_container}>
            <div className={styles.msg_cart_container}>
              {(walletBalance?.astroVCard ?? 0) > 0 && (
                <div className={styles.free_chat_timer}>
                  <span>
                    This is a free chat and will be end in{" "}
                    {secondsElapsed?.minutes?.toString().length > 1
                      ? secondsElapsed?.minutes
                      : `0${secondsElapsed?.minutes ?? 0}`}
                    :
                    {secondsElapsed?.seconds?.toString().length > 1
                      ? secondsElapsed?.seconds
                      : `0${secondsElapsed?.seconds ?? 0}`}{" "}
                  </span>
                </div>
              )}
              {messages.length > 0 && (
                <div className={styles.chat_start}>
                  <span>Chat started</span>
                </div>
              )}
              {messages.map((cElement, index) => {
                return renderMsgCart(cElement, index);
              })}
              <div className="">
                {isTyping && (
                  <div className={`${styles.typing} `}>
                    {/* @ts-expect-error Server Component */}

                    <Lottie
                      width={60}
                      // height={40}
                      options={{ animationData: ANIMATION_DATA }}
                      isClickToPauseDisabled={false}
                      style={{ margin: "0px" }}
                    />
                  </div>
                )}
              </div>
              <LowBalanceChatCart
                show={isLowBalance}
                onClose={() => setIsLowBalance(false)}
              />

              <div ref={messagesEndRef} />
            </div>
            <div className={styles.chat_input_container}>
              <div className={styles.chat_input_section}>
                <input
                  type="text"
                  name="msgInput"
                  value={msgInput}
                  onChange={handleMsgInput}
                  placeholder="Type Something...."
                  className={styles.msg_input}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleSent();
                    }
                  }}
                />

                <div className={styles.file_input_section}>
                  <input
                    type="file"
                    name="file"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      e?.currentTarget?.files?.[0] &&
                      setFileData(e?.currentTarget?.files?.[0])
                    }
                  />
                  <Image
                    src={UPLOAD_FILE_ICON}
                    alt="file"
                    height={21}
                    width={20}
                    className={styles.upload_icon}
                  />
                </div>
              </div>
              <div
                className={`${styles.send_btn_container} ${
                  msgInput.length <= 0 ? styles.in_active : ""
                }`}
                onClick={handleSent}
              >
                <Image
                  src={SEND_ICON}
                  alt="btn"
                  width={28}
                  height={28}
                  className={styles.send_btn}
                />
              </div>
            </div>
          </div>
        </div>
        <Popup show={imagePopup?.length > 0} onClose={() => setImagePopup("")}>
          <div className={styles.expand_image}>
            <Image src={imagePopup} alt="image" height={400} width={400} />
          </div>
        </Popup>

        <ChatEndPopup
          show={isChatEndPopup}
          onClose={() => setIsChatEndPopup(false)}
          onEnd={handleEndChat}
        />
      </div>
    );
  }
};

export default ChatFeed;
